/**
 * Mention trigger — the ONE place that decides when an "@" the user just typed
 * should open the tag picker, and how the picked name gets written back.
 *
 * Every notes composer in the app used to carry its own copy of this logic and
 * they disagreed. The strict family (deal / job / billing / scheduler notes)
 * required the "@" to sit at index 0 or immediately after a literal SPACE:
 *
 *     (lastAtIndex === 0 || value[lastAtIndex - 1] === ' ')
 *
 * so tagging failed the moment a sentence had any shape to it — after a
 * newline ("Site done.\n@reuben"), after a comma, after an opening bracket,
 * after an em-dash. The loose family (MentionTextarea / todos / plate / inbox)
 * had no boundary check at all, so an email address in the note text
 * ("reuben@thetechery.au") popped the picker mid-word.
 *
 * The rule here: an "@" triggers when it starts a word — that is, when the
 * character before it is anything OTHER than a word character or another "@".
 * Punctuation, newlines and brackets are all valid lead-ins; a letter or digit
 * is not, which is what keeps email addresses quiet.
 */

import type { MentionableUser } from '@/types/mention.types';

/**
 * Longest handle fragment we'll keep the picker open for. Past this the "@"
 * is almost certainly historical text the cursor happens to sit after, not a
 * tag in progress.
 */
const MAX_QUERY_LENGTH = 32;

export interface MentionTrigger {
  /** Index of the "@" that opened the picker. */
  atIndex: number;
  /** Text typed between the "@" and the cursor — the search term. */
  query: string;
}

/**
 * Find the in-progress @mention immediately before `cursor`, if there is one.
 *
 * @param text - Full composer text
 * @param cursor - Caret position (selectionStart)
 * @returns The trigger, or null when the picker should stay closed
 */
export function findMentionTrigger(text: string, cursor: number): MentionTrigger | null {
  if (typeof text !== 'string') return null;

  const caret = Math.max(0, Math.min(cursor ?? 0, text.length));
  const before = text.slice(0, caret);
  const atIndex = before.lastIndexOf('@');
  if (atIndex === -1) return null;

  // Word-start boundary. Empty string = start of text, which always qualifies.
  // `\w` covers letters, digits and underscore — so "email@host" and "@@" are
  // both rejected, while ".@", "(@", ",@", "-@", "\n@" and " @" all pass.
  const prev = atIndex > 0 ? before[atIndex - 1] : '';
  if (prev && /[\w@]/.test(prev)) return null;

  const query = before.slice(atIndex + 1);
  // A space means the user has moved on to the next word — the tag is done.
  if (/\s/.test(query)) return null;
  if (query.length > MAX_QUERY_LENGTH) return null;

  return { atIndex, query };
}

/**
 * Replace the in-progress handle with the picked one and report where the
 * caret should land.
 *
 * Everything from the "@" up to the caret is replaced; text after the caret is
 * preserved untouched, so picking a name mid-sentence doesn't eat the rest of
 * the line.
 *
 * @param text - Full composer text
 * @param cursor - Caret position at the moment of the pick
 * @param atIndex - Index of the triggering "@" (from findMentionTrigger)
 * @param handle - Handle to insert, with or without a leading "@"
 */
export function applyMention(
  text: string,
  cursor: number,
  atIndex: number,
  handle: string,
): { text: string; cursor: number } {
  const caret = Math.max(0, Math.min(cursor ?? 0, text.length));
  const start = Math.max(0, Math.min(atIndex, text.length));
  const mention = handle.startsWith('@') ? handle : `@${handle}`;

  // Trailing space so the next word doesn't glue onto the handle — unless the
  // following character already is whitespace, in which case adding another
  // leaves a double gap.
  const rest = text.slice(caret);
  const needsSpace = !/^\s/.test(rest);
  const inserted = needsSpace ? `${mention} ` : mention;

  return {
    text: text.slice(0, start) + inserted + rest,
    cursor: start + inserted.length,
  };
}

/**
 * The handle to write into the note for a picked user.
 *
 * First name when it's unambiguous, `first.last` when two people share a first
 * name. Both forms resolve in `extractMentions`, but a bare first name shared
 * by two people resolves to whoever appears FIRST in the user list — i.e. the
 * notification silently goes to the wrong person. Disambiguating at insert
 * time is the only point where we still know exactly who was picked.
 *
 * @param user - The picked user
 * @param allUsers - Everyone in the picker, used to detect the collision
 */
export function mentionHandleFor(
  user: MentionableUser,
  allUsers: MentionableUser[] = [],
): string {
  const first = (user.firstName || user.name.split(/\s+/)[0] || user.name).toLowerCase();

  const sharesFirstName = allUsers.some((other) => {
    if (other.id === user.id) return false;
    const otherFirst = (
      other.firstName || other.name.split(/\s+/)[0] || other.name
    ).toLowerCase();
    return otherFirst === first;
  });

  if (!sharesFirstName) return `@${first}`;

  // `extractMentions` matches the dotted full name, so this still resolves.
  return `@${user.name.trim().toLowerCase().replace(/\s+/g, '.')}`;
}

/**
 * Does `query` match this user? Same matcher every picker should use —
 * substring against full name, first name and last name.
 */
export function matchesMentionQuery(user: MentionableUser, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return (
    user.name.toLowerCase().includes(q) ||
    (user.firstName?.toLowerCase().includes(q) ?? false) ||
    (user.lastName?.toLowerCase().includes(q) ?? false)
  );
}
